import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import Editor, { DiffEditor, type OnMount } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import './monacoSetup';

type MapperJsonEditorMode = 'edit' | 'diff';

interface JsonMapperMonacoEditorProps {
  readonly original: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly ariaLabel: string;
  readonly isReadOnly?: boolean;
}

interface UndoRedoHistoryState {
  position: number;
  max: number;
}

const EDITOR_OPTIONS = {
  minimap: { enabled: false },
  scrollBeyondLastLine: false,
  fontSize: 13,
  fontFamily: 'Menlo, Consolas, Monaco, monospace',
  wordWrap: 'on' as const,
  automaticLayout: true,
  tabSize: 2,
  insertSpaces: true,
  formatOnPaste: true,
  formatOnType: true,
  lineNumbers: 'on' as const,
  glyphMargin: false,
  folding: true,
  padding: { top: 8, bottom: 8 },
  overviewRulerLanes: 0,
  hideCursorInOverviewRuler: true,
  overviewRulerBorder: false,
  renderLineHighlight: 'line' as const,
};

const DIFF_EDITOR_OPTIONS = {
  renderSideBySide: true,
  useInlineViewWhenSpaceIsLimited: false,
  enableSplitViewResizing: true,
  splitViewDefaultRatio: 0.5,
  readOnly: false,
  originalEditable: false,
  automaticLayout: true,
  scrollBeyondLastLine: false,
  minimap: { enabled: false },
  fontSize: 13,
  fontFamily: 'Menlo, Consolas, Monaco, monospace',
  wordWrap: 'on' as const,
  diffWordWrap: 'on' as const,
  tabSize: 2,
  insertSpaces: true,
  formatOnPaste: true,
  formatOnType: true,
  renderOverviewRuler: true,
};

const INITIAL_UNDO_REDO_HISTORY: UndoRedoHistoryState = { position: 0, max: 0 };

function getToolbarButtonClassName(isActive: boolean): string {
  return `inline-flex h-8 w-8 items-center justify-center rounded-lg border transition-colors disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white dark:disabled:hover:bg-content2 ${
    isActive
      ? 'border-primary bg-primary text-white'
      : 'border-[#d0d5dd] bg-white text-[#344054] hover:bg-[#f9fafb] dark:border-default-200 dark:bg-content2 dark:text-default-600 dark:hover:bg-content3'
  }`;
}

function applyUndoRedoHistory(
  history: UndoRedoHistoryState,
  event: editor.IModelContentChangedEvent
): UndoRedoHistoryState {
  if (event.isFlush) {
    return { ...INITIAL_UNDO_REDO_HISTORY };
  }
  if (event.isUndoing) {
    return {
      position: Math.max(0, history.position - 1),
      max: history.max,
    };
  }
  if (event.isRedoing) {
    return {
      position: Math.min(history.max, history.position + 1),
      max: history.max,
    };
  }
  const nextPosition = history.position + 1;
  return {
    position: nextPosition,
    max: nextPosition,
  };
}

function JsonMapperMonacoEditor({
  original,
  value,
  onChange,
  ariaLabel,
  isReadOnly = false,
}: Readonly<JsonMapperMonacoEditorProps>) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const editorTheme = isDark ? 'vs-dark' : 'vs';
  const containerRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const diffEditorRef = useRef<editor.IStandaloneDiffEditor | null>(null);
  const suppressExternalSyncRef = useRef(false);
  const lastEmittedValueRef = useRef(value);
  const undoRedoHistoryRef = useRef<UndoRedoHistoryState>(INITIAL_UNDO_REDO_HISTORY);
  const [mode, setMode] = useState<MapperJsonEditorMode>('edit');
  const [diffModifiedSeed, setDiffModifiedSeed] = useState(value);
  const [editorHeight, setEditorHeight] = useState(200);
  const [editorWidth, setEditorWidth] = useState<number | string>('100%');
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const syncUndoRedoButtons = useCallback((history: UndoRedoHistoryState) => {
    setCanUndo(history.position > 0);
    setCanRedo(history.position < history.max);
  }, []);

  const emitEditorValue = useCallback(
    (nextValue: string) => {
      suppressExternalSyncRef.current = true;
      lastEmittedValueRef.current = nextValue;
      onChange(nextValue);
    },
    [onChange]
  );

  const getActiveEditor = useCallback((): editor.IStandaloneCodeEditor | null => {
    if (mode === 'edit') {
      return editorRef.current;
    }
    return diffEditorRef.current?.getModifiedEditor() ?? null;
  }, [mode]);

  const syncExternalValueToEditor = useCallback(
    (activeEditor: editor.IStandaloneCodeEditor, nextValue: string) => {
      const currentValue = activeEditor.getModel()?.getValue() ?? '';
      if (currentValue === nextValue) {
        return;
      }
      activeEditor.setValue(nextValue);
      undoRedoHistoryRef.current = { ...INITIAL_UNDO_REDO_HISTORY };
      syncUndoRedoButtons(undoRedoHistoryRef.current);
    },
    [syncUndoRedoButtons]
  );

  const attachEditorListeners = useCallback(
    (activeEditor: editor.IStandaloneCodeEditor) => {
      const handleContentChange = (event: editor.IModelContentChangedEvent) => {
        undoRedoHistoryRef.current = applyUndoRedoHistory(undoRedoHistoryRef.current, event);
        syncUndoRedoButtons(undoRedoHistoryRef.current);

        if (!event.isUndoing && !event.isRedoing && !event.isFlush) {
          emitEditorValue(activeEditor.getValue());
        }
      };

      const contentDisposable = activeEditor.onDidChangeModelContent(handleContentChange);

      return () => {
        contentDisposable.dispose();
      };
    },
    [emitEditorValue, syncUndoRedoButtons]
  );

  const layoutDiffEditor = useCallback(() => {
    requestAnimationFrame(() => {
      diffEditorRef.current?.layout();
    });
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateDimensions = () => {
      const nextHeight = container.clientHeight;
      const nextWidth = container.clientWidth;
      if (nextHeight > 0) {
        setEditorHeight(nextHeight);
      }
      if (nextWidth > 0) {
        setEditorWidth(nextWidth);
      }
      layoutDiffEditor();
    };

    updateDimensions();
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [mode, layoutDiffEditor]);

  useEffect(() => {
    if (suppressExternalSyncRef.current) {
      suppressExternalSyncRef.current = false;
      return;
    }

    if (value === lastEmittedValueRef.current) {
      return;
    }

    const activeEditor = getActiveEditor();
    if (!activeEditor) {
      lastEmittedValueRef.current = value;
      return;
    }

    syncExternalValueToEditor(activeEditor, value);
    lastEmittedValueRef.current = value;
  }, [value, getActiveEditor, syncExternalValueToEditor]);

  const handleEditMount: OnMount = useCallback(
    (activeEditor) => {
      editorRef.current = activeEditor;
      lastEmittedValueRef.current = activeEditor.getValue();
      undoRedoHistoryRef.current = { ...INITIAL_UNDO_REDO_HISTORY };
      syncUndoRedoButtons(undoRedoHistoryRef.current);
      return attachEditorListeners(activeEditor);
    },
    [attachEditorListeners, syncUndoRedoButtons]
  );

  const handleDiffMount = useCallback(
    (diffEditor: editor.IStandaloneDiffEditor) => {
      diffEditorRef.current = diffEditor;
      diffEditor.updateOptions({ ...DIFF_EDITOR_OPTIONS, readOnly: isReadOnly });
      layoutDiffEditor();

      const modifiedEditor = diffEditor.getModifiedEditor();
      lastEmittedValueRef.current = modifiedEditor.getValue();
      undoRedoHistoryRef.current = { ...INITIAL_UNDO_REDO_HISTORY };
      syncUndoRedoButtons(undoRedoHistoryRef.current);
      return attachEditorListeners(modifiedEditor);
    },
    [attachEditorListeners, layoutDiffEditor, syncUndoRedoButtons, isReadOnly]
  );

  const handleUndo = () => {
    const activeEditor = getActiveEditor();
    if (!activeEditor || !canUndo) return;
    activeEditor.trigger('keyboard', 'undo', null);
  };

  const handleRedo = () => {
    const activeEditor = getActiveEditor();
    if (!activeEditor || !canRedo) return;
    activeEditor.trigger('keyboard', 'redo', null);
  };

  const toggleMode = () => {
    setMode((currentMode) => {
      const nextMode = currentMode === 'edit' ? 'diff' : 'edit';
      if (nextMode === 'diff') {
        setDiffModifiedSeed(lastEmittedValueRef.current);
      }
      return nextMode;
    });
  };

  return (
    <div className="relative h-full w-full min-h-0">
      <div className="absolute right-3 top-3 z-20 flex flex-col items-center gap-2">
        <button
          type="button"
          className={getToolbarButtonClassName(false)}
          onClick={handleUndo}
          disabled={!canUndo}
          aria-label={t('Undo')}
          title={t('Undo')}
        >
          <Icon icon="material-symbols:undo-rounded" width="20" />
        </button>
        <button
          type="button"
          className={getToolbarButtonClassName(false)}
          disabled={!canRedo}
          onClick={handleRedo}
          aria-label={t('Redo')}
          title={t('Redo')}
        >
          <Icon icon="material-symbols:redo-rounded" width="20" />
        </button>
        <button
          type="button"
          className={getToolbarButtonClassName(mode === 'diff')}
          onClick={toggleMode}
          aria-label={mode === 'edit' ? t('Show diff') : t('Show editor')}
          aria-pressed={mode === 'diff'}
          title={mode === 'edit' ? t('Show diff') : t('Show editor')}
        >
          <Icon icon="material-symbols:difference-outline-rounded" width="20" />
        </button>
      </div>
      <div
        ref={containerRef}
        className="h-full w-full min-h-0 overflow-hidden rounded-[12px] border border-[#d0d5dd] bg-white dark:border-default-200 dark:bg-content2"
        aria-label={ariaLabel}
      >
        {mode === 'edit' ? (
          <Editor
            height={editorHeight}
            width="100%"
            theme={editorTheme}
            language="json"
            defaultValue={value}
            onMount={handleEditMount}
            options={{
              ...EDITOR_OPTIONS,
              readOnly: isReadOnly,
              ariaLabel,
            }}
            loading={
              <div className="flex h-full items-center justify-center text-sm text-[#98a2b3] dark:text-default-500">
                {t('Loading editor...')}
              </div>
            }
          />
        ) : (
          <DiffEditor
            key="mapper-definition-diff"
            height={editorHeight}
            width={editorWidth}
            theme={editorTheme}
            language="json"
            original={original || '{}'}
            modified={diffModifiedSeed || '{}'}
            onMount={handleDiffMount}
            options={{ ...DIFF_EDITOR_OPTIONS, readOnly: isReadOnly }}
            loading={
              <div className="flex h-full items-center justify-center text-sm text-[#98a2b3] dark:text-default-500">
                {t('Loading editor...')}
              </div>
            }
          />
        )}
      </div>
    </div>
  );
}

export { JsonMapperMonacoEditor };
